import { generateClientId } from "../../../utils/clientId";

export const saveKeyForPatient = id => `patient:${id}`;
export const saveKeyForLine = id => `line:${id}`;
const newLineClientId = () => generateClientId("cno-line-client");
export const lineClientId = line => line?.clientId || (line?.id != null ? `server:${line.id}` : newLineClientId());

export const emptyPatient = (fields = {}) => ({
    id: generateClientId("cno"),
    nom: "",
    prenom: "",
    adresse: "",
    telephone: "",
    cnos: [],
    ...fields,
});

export const emptyLine = () => ({
    id: generateClientId("cno-line"),
    clientId: newLineClientId(),
    produit: "",
    quantite: "",
});

export const normalizeLine = line => ({
    ...line,
    clientId: lineClientId(line),
    produit: line.produit || "",
    quantite: line.quantite ?? "",
});

export const normalizePatients = patients => (Array.isArray(patients) ? patients : []).map(patient => ({
    ...patient,
    nom: patient.nom || "",
    prenom: patient.prenom || "",
    adresse: patient.adresse || "",
    telephone: patient.telephone || "",
    cnos: (patient.cnos || []).map(normalizeLine),
}));

export function patientLabel(patient) {
    const name = `${patient.nom || ""} ${patient.prenom || ""}`.trim();
    return name || "Nouveau patient";
}

export function patientInitials(patient) {
    const initials = `${patient.prenom?.[0] || ""}${patient.nom?.[0] || ""}`.toUpperCase();
    return initials || "NP";
}

export function validateNewPatient(form) {
    if (!form.nom.trim() || !form.prenom.trim()) {
        return { error: "Le nom et le prénom sont obligatoires." };
    }
    return {
        fields: {
            nom: form.nom.trim(),
            prenom: form.prenom.trim(),
            adresse: form.adresse.trim(),
            telephone: form.telephone.trim(),
        },
    };
}

export function toQuantity(value) {
    if (value === "" || value == null) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

export function isCompleteCnoLine(line) {
    return !!line?.produit?.trim() && toQuantity(line.quantite) !== null;
}

// Prépare l'enregistrement d'une ligne : null si la ligne n'est pas complète.
export function buildLineSave(line, patch = {}) {
    const nextLine = { ...line, ...patch };
    if (!isCompleteCnoLine(nextLine)) return null;
    const payload = { ...nextLine, produit: nextLine.produit.trim(), quantite: toQuantity(nextLine.quantite) };
    delete payload.clientId;
    const createPayload = { ...payload };
    delete createPayload.id;
    return { payload, createPayload };
}

export const linesReadyToSave = (patient, statuses) => (patient?.cnos || []).filter(line => (
    statuses[saveKeyForLine(line.clientId)] === "dirty" && isCompleteCnoLine(line)
));

export const replacePatient = (patients, id, replacement) => patients.map(item => (item.id === id ? replacement : item));
export const patchPatient = (patients, id, patch) => patients.map(item => (item.id === id ? { ...item, ...patch } : item));
export const removePatient = (patients, id) => patients.filter(item => item.id !== id);

const mapLines = (patients, patientId, update) => patients.map(patient => (
    patient.id === patientId ? { ...patient, cnos: update(patient.cnos || []) } : patient
));

export const appendLine = (patients, patientId, line) => mapLines(patients, patientId, lines => [...lines, line]);

export const patchLine = (patients, patientId, clientId, patch) => mapLines(patients, patientId, lines => lines.map(line => (
    line.clientId === clientId ? { ...line, ...patch } : line
)));

export const replaceLine = (patients, patientId, clientId, replacement) => mapLines(patients, patientId, lines => lines.map(line => (
    line.clientId === clientId ? replacement : line
)));

export const removeLine = (patients, patientId, deleteKey) => mapLines(patients, patientId, lines => lines.filter(line => (
    (line.clientId || line.id) !== deleteKey
)));

export function reconcileSelectedPatientId(patients, currentId) {
    if (patients.length === 0) return "";
    return patients.some(patient => patient.id === currentId) ? currentId : patients[0].id;
}

export const withStatus = (statuses, key, status) => ({ ...statuses, [key]: status });

export function replaceStatus(statuses, oldKey, newKey, status) {
    const next = { ...statuses };
    delete next[oldKey];
    next[newKey] = status;
    return next;
}

export function dropStatuses(statuses, keys) {
    const next = { ...statuses };
    keys.forEach(key => {
        delete next[key];
    });
    return next;
}

export const patientStatusKeys = patient => [
    saveKeyForPatient(patient.id),
    ...(patient.cnos || []).map(line => saveKeyForLine(line.clientId || line.id)),
];

const saveStatusLabels = {
    dirty: "À enregistrer",
    saving: "Enregistrement",
    saved: "Enregistré",
    error: "Erreur",
};

// Libellé du badge d'enregistrement : rien pour une fiche enregistrée.
export function saveBadgeLabel(status) {
    if (!status || status === "saved") return null;
    return saveStatusLabels[status] || "Enregistré";
}
